import React, { useCallback, useMemo, useEffect } from 'react';
import { TMemberInfo } from '@repo/utils';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { MemberInformation } from '../MemberInformation/MemberInformation';
import { getFeatureFlagsFromStorage } from '@src/utills/storage';
import { CboSearchForm } from '@repo/ui';
import { API } from '@src/api';
import { Breadcumbs, ScreenConstants } from '../BreadCumbs/Breadcrumbs';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Link } from '@uhg-abyss/web/ui/Link';
import { useAtom, useAtomValue } from 'jotai';
import { identifiedNeedsAtom, vendorAtom } from '@src/global-state';
import { Button } from '@uhg-abyss/web/ui/Button';

export type CboSearchTopPanelProps = {
  member: TMemberInfo;
  changedZipcode: (zipcode: string) => void;
  findhelpServices: any[];
  uniteusServices: any[];
  vendor: 'findhelp' | 'Unite Us';
  defaultZipcode?: string;
  defaultCategoryLabel?: string;
  defaultCategoryId?: string | number | undefined;
  isUniteUsApiEnabled: boolean;

  onClickNeed: (need: { need: string; needId: string }) => void;
  onVendorChange?: (vendor: 'findhelp' | 'Unite Us') => void;
  onSubmit: (
    category: {
      id: string | number | null;
      label: string;
    },
    zipcode: string
  ) => void;
};

export function CboSearchTopPanel({
  member,
  findhelpServices,
  uniteusServices,
  vendor,
  defaultZipcode,
  defaultCategoryLabel,
  defaultCategoryId,
  onClickNeed,
  onSubmit,
  onVendorChange,
  isUniteUsApiEnabled,
  changedZipcode,
}: CboSearchTopPanelProps): React.JSX.Element {
  const identifiedNeeds = useAtomValue(identifiedNeedsAtom);
  const [vendorCache, setVendorCache] = useAtom(vendorAtom);

  const memberFirstName = useMemo(() => {
    return member.first_name ?? '';
  }, [member]);

  const showMemberInfo: boolean = useMemo(() => {
    return vendor === 'findhelp' || isUniteUsApiEnabled;
  }, [isUniteUsApiEnabled, vendor]);

  const showBenefits: boolean = useMemo(() => {
    const isMedSup = member?.medSup;
    return !isMedSup;
  }, [member]);
  const handleZipcodeChange = useCallback((zipcode: string) => {
    if (changedZipcode) {
      changedZipcode(zipcode);
    }
  }, [changedZipcode]);
  const [showPlanBenefits, setShowPlanBenefits] =
    React.useState<boolean>(false);

  useEffect(() => {
    async function fetchBenefitPlan(): Promise<void> {
      if (
        member?.benefitPlanPlatform !== undefined &&
        member?.benefitPlanID !== undefined &&
        member?.benefitPlanPlatform !== null &&
        member?.benefitPlanID !== null
      ) {
        const resp = await API.getBenefitCheck(
          member?.benefitPlanID,
          member?.benefitPlanPlatform
        );
        if (
          resp !== null &&
          resp !== undefined &&
          'status' in resp &&
          resp.status === 'success' &&
          'data' in resp
        ) {
          setShowPlanBenefits(resp.data?.isSearchAvailable);
        }
      }
    }
    if (
      !member?.medSup &&
      member?.lineOfBusiness !== 'E&I' &&
      member?.lineOfBusiness !== 'IFP'
    ) {
      fetchBenefitPlan();
    }
  }, [member]);

  return (
    <div
      style={{
        // pastel blue
        backgroundColor: '#EDF8FA',
        paddingRight: '5rem',
        paddingLeft: '5rem',
        paddingTop: '2rem',
        paddingBottom: '2rem',
      }}
    >
      <Grid
        style={{
          marginTop: 8,
        }}
      >
        <Grid.Col span={{ xs: 12, md: 8, lg: 8 }}>
          <Breadcumbs screen={ScreenConstants.CBO_SEARCH} />

          {memberFirstName && (
            <Heading serif>Find a resource for {memberFirstName}</Heading>
          )}

          {/* Benefits stuff, if applicable */}
          {showBenefits &&
            member?.lineOfBusiness !== 'IFP' &&
            member?.lineOfBusiness !== 'E&I' && (
              <BenefitsPanel showPlanBenefits={showPlanBenefits} />
            )}

          <Heading
            size="sm"
            style={{
              marginTop: 8,
              marginBottom: 8,
            }}
          >
            Search community-based resources
          </Heading>

          <IdentifiedNeedsPanel
            identifiedNeeds={identifiedNeeds ?? undefined}
            onClickNeed={onClickNeed}
          />

          {/* Search form */}
          {vendor === 'findhelp' || isUniteUsApiEnabled ? (
            <CboSearchForm
              vendor={vendor}
              changedZipcode={handleZipcodeChange}
              defaultZipcode={defaultZipcode}
              defaultCategory={defaultCategoryLabel}
              defaultCategoryId={defaultCategoryId}
              findhelpCategoryData={findhelpServices}
              uniteUsCategoryData={uniteusServices}
              fetchLocationInfo={async (pattern: string) => {
                try {
                  const result = await API.zipcodeSearch({ pattern });
                  return result?.data;
                } catch (error: any) {
                  console.error(
                    'Something bad happened while fetching zipcode search results: ',
                    error
                  );
                }

                return null;
              }}
              fetchProvider={async (zipcode: string) => {
                try {
                  // Use cached vendor when the zipcode matches; otherwise fetch and cache.
                  let result = vendorCache?.zipcode === zipcode ? vendorCache.response : null;
                  if (!result) {
                    result = await API.fetchProviderToUse({
                      lineOfBusiness: member?.lineOfBusiness,
                      zipcode,
                    });
                    if (result?.status === 'success') {
                      setVendorCache({ zipcode, response: result });
                      onVendorChange?.(result.provider.name);
                    }
                  }
                  if (result === null) {
                    throw new Error('Could not fetch provider');
                  }

                  return result.provider;
                } catch (error) {
                  console.error(
                    'Something bad happened while fetching next provider: ',
                    error
                  );
                }

                return null;
              }}
              onSubmit={onSubmit}
            />
          ) : (
            ''
          )}
        </Grid.Col>

        <Grid.Col span={{ xs: 12, md: 4, lg: 4 }}>
          {showMemberInfo && (
            <MemberInformation
              name={`${member?.first_name} ${member.last_name}`}
              memberId={member?.member_id || ''}
              dob={member?.DOB || ''}
              zipcode={member?.zipCode || ''}
              lob={member?.lineOfBusiness || ''}
              showAssessmentButton={
                isUniteUsApiEnabled
                  ? !member?.medSup &&
                    sessionStorage.getItem('UHC-CC-Screening') === 'false'
                  : vendor !== 'Unite Us' &&
                    !member?.medSup &&
                    sessionStorage.getItem('UHC-CC-Screening') === 'false'
              }
            />
          )}
        </Grid.Col>
      </Grid>
    </div>
  );
}

function EmphasisText({ children }): React.JSX.Element {
  return (
    <span style={{ fontSize: '1.125rem', color: '#003C71' }}>
      {children}
    </span>
  );
}

function IdentifiedNeedsPanel({
  identifiedNeeds,
  onClickNeed,
}: {
  identifiedNeeds?: {
    need: string;
    needId: string;
  }[];
  onClickNeed: (need: { need: string; needId: string }) => void;
}): React.JSX.Element {
  return (
    <React.Fragment>
      {identifiedNeeds && (
        <div
          style={{
            marginBottom: 12,
          }}
        >
          <div
            style={{
              marginTop: 4,
              marginBottom: 4,
            }}
          >
            <Text color="#003C71" fontSize="lg">
              Search by selecting any currently identified need
            </Text>
          </div>

          {identifiedNeeds?.map((need) => {
            return (
              <span
                key={`need-button-${need.needId}`}
                style={{ paddingLeft: 0 }}
              >
                <Button
                  style={{
                    backgroundColor: '#C4DFF6',
                    color: 'black',
                    marginLeft: 0,
                    marginTop: 8,
                    marginRight: 8,
                  }}
                  onClick={() => {
                    return onClickNeed(need);
                  }}
                >
                  {need.need}
                </Button>
              </span>
            );
          })}
        </div>
      )}
    </React.Fragment>
  );
}

export function BenefitsPanel({
  showPlanBenefits,
}: {
  showPlanBenefits: boolean;
}): React.JSX.Element | null {
  if (!showPlanBenefits) {
    return null;
  }
  return (
    <div>
      <Heading
        size="sm"
        style={{
          marginBottom: 8,
        }}
      >
        Check plan benefits
      </Heading>

      <EmphasisText>
        Ensure member is aware of available{' '}
        <Link size="$lg" href="/planbenefits">
          Plan Benefits
        </Link>
      </EmphasisText>
    </div>
  );
}
